import { useState, useEffect, useMemo } from 'react';
import { getAllOperators, type OperatorResponse } from '@/api/operators.api';
import { assignOperatorsToMachine, type MachineResponse } from '@/api/machine.api';
import { useToast } from '@/contexts/ToastContext';

interface AssignOperatorsModalProps {
    open: boolean;
    machine: {
        id: string;
        name: string;
        machineCode?: string;
        workCenterName?: string | null;
        assignedOperatorIds?: string[];
    } | null;
    onClose: () => void;
    onSuccess: (updatedMachine: MachineResponse) => void;
}

export default function AssignOperatorsModal({
    open,
    machine,
    onClose,
    onSuccess,
}: AssignOperatorsModalProps) {
    const toast = useToast();
    const [operators, setOperators] = useState<OperatorResponse[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [search, setSearch] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Fetch operators when modal opens
    useEffect(() => {
        if (!open) {
            setSearch('');
            return;
        }

        const initialSelected = new Set(
            (machine?.assignedOperatorIds || []).map(String)
        );
        setSelectedIds(initialSelected);

        const fetchOperators = async () => {
            setIsLoading(true);
            try {
                const res = await getAllOperators();
                if (res.success && res.data) {
                    setOperators(res.data);
                }
            } catch (err) {
                toast.error(err instanceof Error ? err.message : 'Failed to load operators');
            } finally {
                setIsLoading(false);
            }
        };

        fetchOperators();
    }, [open, machine]);

    const filteredOperators = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return operators;
        return operators.filter((op) =>
            op.name.toLowerCase().includes(q) ||
            (op.employee_code && op.employee_code.toLowerCase().includes(q)) ||
            (op.skill && op.skill.toLowerCase().includes(q)) ||
            (op.skill_level && op.skill_level.toLowerCase().includes(q))
        );
    }, [operators, search]);

    const toggleOperator = (opId: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(opId)) {
                next.delete(opId);
            } else {
                next.add(opId);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        const allFilteredIds = filteredOperators.map((op) => op.id);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            allFilteredIds.forEach((id) => next.add(id));
            return next;
        });
    };

    const handleDeselectAll = () => {
        const filteredSet = new Set(filteredOperators.map((op) => op.id));
        setSelectedIds((prev) => {
            const next = new Set(prev);
            filteredSet.forEach((id) => next.delete(id));
            return next;
        });
    };

    const handleSave = async () => {
        if (!machine) return;
        setIsSaving(true);
        try {
            const operatorIdsArray = Array.from(selectedIds);
            const res = await assignOperatorsToMachine(machine.id, operatorIdsArray);
            if (res.success && res.data) {
                toast.success(`Operators assigned to ${machine.name} successfully`);
                onSuccess(res.data);
                onClose();
            } else {
                toast.error(res.message || 'Failed to assign operators');
            }
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Error saving operator assignments');
        } finally {
            setIsSaving(false);
        }
    };

    if (!open || !machine) return null;

    return (
        <div className="fixed inset-0 z-[700] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
                onClick={onClose}
            />

            {/* Modal Dialog */}
            <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200/90 w-full max-w-xl overflow-hidden flex flex-col max-h-[88vh] z-10 animate-in fade-in zoom-in-95 duration-150">
                {/* Header */}
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-[#4f46e5]">
                            <i className="ri-user-settings-line text-lg" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-[#1e293b]">
                                Assign Operators to Machine
                            </h3>
                            <div className="flex items-center gap-2 text-xs text-[#64748b] mt-0.5">
                                {machine.machineCode && (
                                    <span className="font-mono font-semibold text-[#4f46e5] bg-indigo-50 px-1.5 py-0.5 rounded text-[11px] border border-indigo-100">
                                        {machine.machineCode}
                                    </span>
                                )}
                                <span className="font-medium text-[#1e293b]">{machine.name}</span>
                                {machine.workCenterName && (
                                    <span className="text-slate-400">• {machine.workCenterName}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                        title="Close"
                    >
                        <i className="ri-close-line text-lg" />
                    </button>
                </div>

                {/* Search & Selection Controls */}
                <div className="p-4 border-b border-slate-100 bg-white space-y-3">
                    <div className="relative">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search operator by name, employee code, or skill..."
                            className="w-full pl-9 pr-3 py-2 text-xs rounded-lg border border-slate-200 focus:outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5]/20 bg-slate-50/50"
                        />
                        {search && (
                            <button
                                onClick={() => setSearch('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
                            >
                                <i className="ri-close-circle-fill text-sm" />
                            </button>
                        )}
                    </div>

                    <div className="flex items-center justify-between text-xs text-[#64748b]">
                        <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-[#4f46e5] bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100">
                                {selectedIds.size} selected
                            </span>
                            <span className="text-slate-400">of {operators.length} total operators</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={handleSelectAll}
                                className="text-[11px] font-semibold text-[#4f46e5] hover:underline cursor-pointer"
                            >
                                Select All Filtered
                            </button>
                            <span className="text-slate-300">|</span>
                            <button
                                type="button"
                                onClick={handleDeselectAll}
                                className="text-[11px] font-semibold text-slate-500 hover:underline cursor-pointer"
                            >
                                Clear Selection
                            </button>
                        </div>
                    </div>
                </div>

                {/* Operators List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-[220px] max-h-[360px]">
                    {isLoading ? (
                        <div className="py-12 text-center">
                            <i className="ri-loader-4-line text-2xl text-[#4f46e5] animate-spin block mb-2" />
                            <p className="text-xs text-slate-500 font-medium">Loading operators...</p>
                        </div>
                    ) : filteredOperators.length === 0 ? (
                        <div className="py-12 text-center text-slate-400">
                            <i className="ri-user-unfollow-line text-3xl mb-1 block" />
                            <p className="text-xs font-semibold text-slate-600">No operators found</p>
                            <p className="text-[11px] mt-0.5">
                                {search ? 'Try adjusting your search criteria' : 'No operators registered in the system'}
                            </p>
                        </div>
                    ) : (
                        filteredOperators.map((op) => {
                            const isSelected = selectedIds.has(op.id);
                            return (
                                <div
                                    key={op.id}
                                    onClick={() => toggleOperator(op.id)}
                                    className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer ${
                                        isSelected
                                            ? 'bg-indigo-50/50 border-indigo-200/90 shadow-2xs'
                                            : 'bg-white border-slate-200/70 hover:border-slate-300 hover:bg-slate-50/60'
                                    }`}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleOperator(op.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-4 h-4 rounded border-slate-300 text-[#4f46e5] focus:ring-[#4f46e5] cursor-pointer"
                                        />
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className="text-xs font-semibold text-[#1e293b] truncate">
                                                    {op.name}
                                                </p>
                                                {op.employee_code && (
                                                    <span className="font-mono text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.2 rounded border border-slate-200">
                                                        {op.employee_code}
                                                    </span>
                                                )}
                                                {!op.is_active && (
                                                    <span className="text-[9px] bg-red-50 text-red-600 px-1.5 py-0.2 rounded border border-red-200">
                                                        Inactive
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-0.5">
                                                <span className="font-medium text-slate-700">{op.skill}</span>
                                                {op.skill_level && (
                                                    <>
                                                        <span className="text-slate-300">•</span>
                                                        <span className="text-[10px] uppercase tracking-wider text-[#4f46e5] font-semibold">
                                                            {op.skill_level}
                                                        </span>
                                                    </>
                                                )}
                                                {op.shift_name && (
                                                    <>
                                                        <span className="text-slate-300">•</span>
                                                        <span className="text-slate-400">
                                                            <i className="ri-time-line text-[10px] mr-0.5" />
                                                            {op.shift_name}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="shrink-0 pl-2">
                                        {isSelected ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#4f46e5] bg-indigo-50 border border-indigo-200/80 px-2 py-0.5 rounded-full">
                                                <i className="ri-check-line text-xs" />
                                                Assigned
                                            </span>
                                        ) : (
                                            <span className="text-[11px] text-slate-400 hover:text-slate-600">
                                                Click to assign
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3.5 border-t border-slate-100 bg-slate-50/70 flex items-center justify-between">
                    <p className="text-[11px] text-[#64748b]">
                        Operators will be synchronized with machine permissions.
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSaving}
                            className="px-3.5 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={isSaving || isLoading}
                            className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-[#4f46e5] text-white hover:bg-indigo-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 cursor-pointer shadow-xs"
                        >
                            {isSaving && <i className="ri-loader-4-line animate-spin text-sm" />}
                            <span>Save Assignments</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
